/**
 * ミニゲーム統合レジストリ
 *
 * 全ミニゲームのメタデータを一元管理し、
 * Player/Studio 両方から参照可能な Single Source of Truth を提供します。
 */
import { InvestigationConfigSchema, FingerprintConfigSchema, CipherConfigSchema, TimelineConfigSchema, EvidenceBoardConfigSchema, ComparisonConfigSchema, ContradictionConfigSchema, ChaseConfigSchema, IntenseChaseConfigSchema, ContradictionV2ConfigSchema, InterrogationConfigSchema, DeductionConfigSchema, CipherV2ConfigSchema, DoorSelectionConfigSchema, ExistenceErosionConfigSchema, TimingEscapeConfigSchema, HorrorQTEConfigSchema, MrLiquidConfigSchema, SomaCallingFireConfigSchema, } from './minigameConfig';
/**
 * ミニゲームレジストリ（templateId 1-20・10欠番＝実19本）
 * 内訳: Foundation 17（templateId 1-9, 11-18）+ bespoke 2（19=Mr.リキッド, 20=杣川）
 * SSoT: packages/shared/src/minigameConfig.ts
 * 詳細: docs/prod/specs/minigame-config-spec.md §6
 */
export const MINIGAME_REGISTRY = [
    {
        templateId: 1,
        registryId: 'minigame_investigation_01',
        name: '現場捜索',
        nameEn: 'Investigation',
        schema: InvestigationConfigSchema,
        playerComponent: 'Minigame01_Investigation',
        studioWysiwyg: 'InvestigationV2WysiwygEditor',
        studioQuickEditor: 'MinigameQuickSettings',
        status: 'stable',
        description: '画面の証拠をタップして発見（順番/順不同モード対応）',
        category: 'exploration',
        tags: ['discovery', 'investigation'],
        recommendedGenres: ['mystery', 'coc', 'horror'],
        difficulty: 1,
        estimatedDuration: '30s-1min',
    },
    {
        templateId: 2,
        registryId: 'minigame_fingerprint_02',
        name: '指紋採取',
        nameEn: 'Fingerprint',
        schema: FingerprintConfigSchema,
        playerComponent: 'Minigame02_Fingerprint',
        studioQuickEditor: 'MinigameQuickSettings',
        status: 'stable',
        description: '指紋を手順通りに処理',
        category: 'puzzle',
        tags: ['investigation', 'memory'],
        recommendedGenres: ['mystery'],
        difficulty: 2,
        estimatedDuration: '1-2min',
    },
    {
        templateId: 3,
        registryId: 'minigame_cipher_03',
        name: '暗号ダイヤル',
        nameEn: 'Cipher',
        schema: CipherConfigSchema,
        playerComponent: 'Minigame03_Cipher',
        studioQuickEditor: 'MinigameQuickSettings',
        status: 'stable',
        description: 'ダイヤルを回して暗号を解読',
        category: 'puzzle',
        tags: ['input', 'mystery'],
        recommendedGenres: ['mystery', 'adventure'],
        difficulty: 2,
        estimatedDuration: '1-2min',
    },
    {
        templateId: 4,
        registryId: 'minigame_timeline_04',
        name: 'タイムライン',
        nameEn: 'Timeline',
        schema: TimelineConfigSchema,
        playerComponent: 'Minigame04_Timeline',
        studioQuickEditor: 'MinigameQuickSettings',
        status: 'stable',
        description: 'イベントを時系列順に並べる',
        category: 'logic',
        tags: ['memory', 'mystery'],
        recommendedGenres: ['mystery'],
        difficulty: 2,
        estimatedDuration: '1-2min',
    },
    {
        templateId: 5,
        registryId: 'minigame_evidence_05',
        name: '証拠ボード',
        nameEn: 'EvidenceBoard',
        schema: EvidenceBoardConfigSchema,
        playerComponent: 'Minigame05_EvidenceBoard',
        studioWysiwyg: 'EvidenceBoardWysiwygEditor',
        studioQuickEditor: 'MinigameQuickSettings',
        status: 'stable',
        description: '証拠同士を線で接続',
        category: 'logic',
        tags: ['investigation', 'mystery'],
        recommendedGenres: ['mystery'],
        difficulty: 2,
        estimatedDuration: '1-2min',
    },
    {
        templateId: 6,
        registryId: 'minigame_comparison_06',
        name: '比較検証',
        nameEn: 'Comparison',
        schema: ComparisonConfigSchema,
        playerComponent: 'Minigame06_Comparison',
        studioWysiwyg: 'ComparisonWysiwygEditor',
        studioQuickEditor: 'MinigameQuickSettings',
        status: 'stable',
        description: '2つの画像から違いを見つける',
        category: 'exploration',
        tags: ['discovery', 'investigation'],
        recommendedGenres: ['mystery', 'horror'],
        difficulty: 1,
        estimatedDuration: '30s-1min',
    },
    {
        templateId: 7,
        registryId: 'minigame_contradiction_07',
        name: '矛盾指摘',
        nameEn: 'Contradiction',
        schema: ContradictionConfigSchema,
        playerComponent: 'Minigame07_Contradiction',
        studioQuickEditor: 'MinigameQuickSettings',
        status: 'stable',
        description: '会話の矛盾を指摘',
        category: 'logic',
        tags: ['mystery', 'psychological'],
        recommendedGenres: ['mystery'],
        difficulty: 2,
        estimatedDuration: '1-2min',
    },
    {
        templateId: 8,
        registryId: 'minigame_chase_08',
        name: '追跡チェイス',
        nameEn: 'Chase',
        schema: ChaseConfigSchema,
        playerComponent: 'Minigame08_Chase',
        studioQuickEditor: 'MinigameQuickSettings',
        status: 'stable',
        description: 'レーンを切り替えて追跡',
        category: 'action',
        tags: ['chase', 'timing'],
        recommendedGenres: ['adventure', 'horror', 'coc'],
        difficulty: 2,
        estimatedDuration: '30s-1min',
    },
    {
        templateId: 9,
        registryId: 'minigame_quickcipher_09',
        name: '緊迫チェイス',
        nameEn: 'IntenseChase',
        schema: IntenseChaseConfigSchema,
        playerComponent: 'Minigame09_IntenseChase',
        studioQuickEditor: 'MinigameQuickSettings',
        status: 'stable',
        description: 'タイミング入力で追う/逃げる',
        category: 'action',
        tags: ['chase', 'timing', 'survival'],
        recommendedGenres: ['horror', 'coc', 'adventure'],
        difficulty: 2,
        estimatedDuration: '30s-1min',
    },
    {
        templateId: 11,
        registryId: 'minigame_contradiction_11',
        name: '矛盾突き付け',
        nameEn: 'ContradictionV2',
        schema: ContradictionV2ConfigSchema,
        playerComponent: 'Minigame11_ContradictionMatch',
        studioWysiwyg: 'ContradictionV2WysiwygEditor',
        studioQuickEditor: 'ContradictionV2QuickEditor',
        status: 'stable',
        description: '証拠と証言の対比で矛盾を突く',
        category: 'logic',
        tags: ['mystery', 'psychological', 'investigation'],
        recommendedGenres: ['mystery'],
        difficulty: 3,
        estimatedDuration: '2-3min',
    },
    {
        templateId: 12,
        registryId: 'minigame_interrogation_12',
        name: '尋問',
        nameEn: 'Interrogation',
        schema: InterrogationConfigSchema,
        playerComponent: 'Minigame12_Interrogation',
        studioQuickEditor: 'MinigameQuickSettings',
        status: 'stable',
        description: '容疑者との対話で真実を引き出す',
        category: 'dialogue',
        tags: ['communication', 'psychological', 'mystery'],
        recommendedGenres: ['mystery', 'coc'],
        difficulty: 3,
        estimatedDuration: '3-5min',
    },
    {
        templateId: 13,
        registryId: 'minigame_deduction_13',
        name: '推理クイズ',
        nameEn: 'Deduction',
        schema: DeductionConfigSchema,
        playerComponent: 'Minigame13_Deduction',
        studioQuickEditor: 'MinigameQuickSettings',
        status: 'stable',
        description: '収集した情報から結論を導く',
        category: 'logic',
        tags: ['mystery', 'choice'],
        recommendedGenres: ['mystery'],
        difficulty: 2,
        estimatedDuration: '1-2min',
    },
    {
        templateId: 14,
        registryId: 'minigame_cipherv2_14',
        name: '暗号解読V2',
        nameEn: 'CipherV2',
        schema: CipherV2ConfigSchema,
        playerComponent: 'Minigame14_CipherV2',
        studioQuickEditor: 'MinigameQuickSettings',
        status: 'stable',
        description: '暗号文を解読して隠されたメッセージを発見',
        category: 'puzzle',
        tags: ['input', 'mystery'],
        recommendedGenres: ['mystery', 'adventure'],
        difficulty: 3,
        estimatedDuration: '2-5min',
    },
    // Template 15-17: TRPG向け新ミニゲーム
    {
        templateId: 15,
        registryId: 'minigame_doorselection_15',
        name: '選択の恐怖',
        nameEn: 'DoorSelection',
        schema: DoorSelectionConfigSchema,
        playerComponent: 'Minigame15_DoorSelection',
        studioQuickEditor: 'MinigameQuickSettings',
        status: 'stable',
        description: '複数の扉/選択肢から正解を探す緊張感のある選択',
        category: 'exploration',
        tags: ['choice', 'horror', 'psychological'],
        recommendedGenres: ['horror', 'coc'],
        difficulty: 1,
        estimatedDuration: '30s-1min',
    },
    {
        templateId: 16,
        registryId: 'minigame_existenceerosion_16',
        name: '存在の侵食',
        nameEn: 'ExistenceErosion',
        schema: ExistenceErosionConfigSchema,
        playerComponent: 'Minigame16_ExistenceErosion',
        studioQuickEditor: 'MinigameQuickSettings',
        status: 'stable',
        description: 'SAN値や存在度の減少をタイミングゲームで表現',
        category: 'action',
        tags: ['timing', 'horror', 'psychological', 'survival'],
        recommendedGenres: ['horror', 'coc'],
        difficulty: 2,
        estimatedDuration: '30s-1min',
    },
    {
        templateId: 17,
        registryId: 'minigame_timingescape_17',
        name: '逃走タイミング',
        nameEn: 'TimingEscape',
        schema: TimingEscapeConfigSchema,
        playerComponent: 'Minigame17_TimingEscape',
        studioQuickEditor: 'MinigameQuickSettings',
        status: 'stable',
        description: 'タイミングよくタップして追跡者から逃げる',
        category: 'action',
        tags: ['timing', 'chase', 'survival', 'horror'],
        recommendedGenres: ['horror', 'coc', 'adventure'],
        difficulty: 2,
        estimatedDuration: '30s-1min',
    },
    {
        templateId: 18,
        registryId: 'minigame_horrorqte_18',
        name: 'ホラーQTE',
        nameEn: 'HorrorQTE',
        schema: HorrorQTEConfigSchema,
        playerComponent: 'Minigame18_HorrorQTE',
        studioQuickEditor: 'MinigameQuickSettings',
        status: 'stable',
        description: 'ホラー演出を重視したタイミング入力QTE（鼓動・ビネット・暗転）',
        category: 'action',
        tags: ['timing', 'horror', 'psychological', 'survival'],
        recommendedGenres: ['horror', 'coc'],
        difficulty: 2,
        estimatedDuration: '30s-1min',
    },
    {
        templateId: 19,
        registryId: 'minigame_mr_liquid_19',
        name: 'Mr.リキッド能力発動',
        nameEn: 'MrLiquidAbility',
        schema: MrLiquidConfigSchema,
        playerComponent: 'Minigame19_MrLiquid',
        studioQuickEditor: 'MinigameQuickSettings',
        status: 'stable',
        description: '液膜探索・水鏡合わせ・放水バトル・消火ラインを切り替える街歩き専用ミニゲーム',
        category: 'exploration',
        tags: ['discovery', 'investigation', 'timing'],
        recommendedGenres: ['mystery', 'adventure', 'fantasy'],
        difficulty: 2,
        estimatedDuration: '30s-2min',
    },
    {
        templateId: 20,
        registryId: 'minigame_soma_calling_fire_20',
        name: '杣川の呼び火',
        nameEn: 'SomaCallingFire',
        schema: SomaCallingFireConfigSchema,
        playerComponent: 'Minigame20_SomaCallingFire',
        studioQuickEditor: 'MinigameQuickSettings',
        status: 'beta',
        description: '川の断片採集・傷ノイズ観測・声の復元を切り替える杣川シナリオ専用ミニゲーム',
        category: 'exploration',
        tags: ['discovery', 'ritual', 'horror', 'memory'],
        recommendedGenres: ['fantasy', 'adventure', 'mystery'],
        difficulty: 2,
        estimatedDuration: '30s-3min',
    },
];
// ============================================
// ヘルパー関数
// ============================================
/**
 * テンプレートIDからエントリを取得
 */
export function getMinigameEntry(templateId) {
    return MINIGAME_REGISTRY.find((e) => e.templateId === templateId);
}
/**
 * レジストリIDからエントリを取得
 */
export function getMinigameEntryByRegistryId(registryId) {
    return MINIGAME_REGISTRY.find((e) => e.registryId === registryId);
}
/**
 * テンプレートIDからスキーマを取得
 */
export function getMinigameSchema(templateId) {
    return getMinigameEntry(templateId)?.schema;
}
/**
 * 全テンプレートIDを取得
 */
export function getAllMinigameTemplateIds() {
    return MINIGAME_REGISTRY.map((e) => e.templateId);
}
/**
 * 実装済み（stable/beta）のテンプレートIDを取得
 */
export function getImplementedMinigameTemplateIds() {
    return MINIGAME_REGISTRY
        .filter((e) => e.status === 'stable' || e.status === 'beta')
        .map((e) => e.templateId);
}
/**
 * スタブ状態のテンプレートIDを取得
 */
export function getStubMinigameTemplateIds() {
    return MINIGAME_REGISTRY
        .filter((e) => e.status === 'stub')
        .map((e) => e.templateId);
}
/**
 * テンプレートIDから表示名を取得
 */
export function getMinigameName(templateId) {
    return getMinigameEntry(templateId)?.name ?? `Unknown (${templateId})`;
}
/**
 * テンプレートIDからレジストリIDを取得
 */
export function getMinigameRegistryId(templateId) {
    return getMinigameEntry(templateId)?.registryId;
}
/**
 * templateId → registryId マッピング（後方互換）
 */
export const MINIGAME_TEMPLATE_TO_REGISTRY = Object.fromEntries(MINIGAME_REGISTRY.map((e) => [e.templateId, e.registryId]));
/**
 * registryId → templateId マッピング（後方互換）
 */
export const MINIGAME_REGISTRY_TO_TEMPLATE = Object.fromEntries(MINIGAME_REGISTRY.map((e) => [e.registryId, e.templateId]));
// ============================================
// カテゴリ/タグ/ジャンル フィルタリング関数
// ============================================
/**
 * カテゴリでフィルタリング
 */
export function getMinigamesByCategory(category) {
    return MINIGAME_REGISTRY.filter((e) => e.category === category && e.status !== 'stub');
}
/**
 * タグでフィルタリング（AND条件: 全てのタグを含む）
 */
export function getMinigamesByTags(tags) {
    return MINIGAME_REGISTRY.filter((e) => e.status !== 'stub' && tags.every((tag) => e.tags.includes(tag)));
}
/**
 * タグでフィルタリング（OR条件: いずれかのタグを含む）
 */
export function getMinigamesByAnyTag(tags) {
    return MINIGAME_REGISTRY.filter((e) => e.status !== 'stub' && tags.some((tag) => e.tags.includes(tag)));
}
/**
 * シナリオジャンルで推奨ミニゲームを取得
 */
export function getMinigamesByGenre(genre) {
    return MINIGAME_REGISTRY.filter((e) => e.status !== 'stub' && e.recommendedGenres.includes(genre));
}
/**
 * 難易度でフィルタリング
 */
export function getMinigamesByDifficulty(difficulty) {
    return MINIGAME_REGISTRY.filter((e) => e.status !== 'stub' && e.difficulty === difficulty);
}
export function filterMinigames(options) {
    return MINIGAME_REGISTRY.filter((e) => {
        // スタブ除外
        if (!options.includeStub && e.status === 'stub')
            return false;
        // カテゴリ
        if (options.category && e.category !== options.category)
            return false;
        // タグ
        if (options.tags && options.tags.length > 0) {
            const mode = options.tagsMode ?? 'or';
            if (mode === 'and') {
                if (!options.tags.every((tag) => e.tags.includes(tag)))
                    return false;
            }
            else {
                if (!options.tags.some((tag) => e.tags.includes(tag)))
                    return false;
            }
        }
        // ジャンル
        if (options.genre && !e.recommendedGenres.includes(options.genre))
            return false;
        // 難易度
        if (options.difficulty && e.difficulty !== options.difficulty)
            return false;
        // テキスト検索
        if (options.searchText) {
            const search = options.searchText.toLowerCase();
            const matchName = e.name.toLowerCase().includes(search);
            const matchNameEn = e.nameEn.toLowerCase().includes(search);
            const matchDesc = e.description?.toLowerCase().includes(search) ?? false;
            if (!matchName && !matchNameEn && !matchDesc)
                return false;
        }
        return true;
    });
}
//# sourceMappingURL=minigameRegistry.js.map